import { CheckCircleIcon, LinkedinIcon, StarIcon } from "lucide-react";
import { BrandHeader } from "../SignUp/BrandSignUp";
import { Button } from "@/shadcnComponents/ui/button";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { twMerge } from "tailwind-merge";
const host: string = import.meta.env.VITE_BACKEND_SERVER_URL;
import { Dialog } from "@/shadcnComponents/ui/dialog";
import { Label } from "@/shadcnComponents/ui/label";
import { Textarea } from "@/shadcnComponents/ui/textarea";
import { LoginForVerifyCollab } from "./LoginForVerifyCollab";
import mixpanel from "mixpanel-browser";
import { useStaticData } from "@/Providers/Data";

export const CollabRatingForm = () => {
  const { link } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const staticDataState = useStaticData();

  const [collabFeedbackData, setCollabFeedbackData] = useState({
    collabData: {},
    influencerData: {},
  });
  const [rating, setRating] = useState(0);
  const [openLoginPopup, setOpenLoginPopup] = useState(false);
  const [review, setReview] = useState("");
  const [localStorageName, setLocalStorageName] = useState(`${link}_rating`);
  const [isLoading, setIsLoading] = useState(false);
  const [masterCollabGoals, setMasterCollabGoals] = useState([]);

  useEffect(() => {
    mixpanel.track("Brand Collab Rating Form Page visited", {
      link: link,
    });

    const fetchMasterData = async () => {
      staticDataState?.getNewDataFromType("Goal / KPI").then((e) => {
        setMasterCollabGoals(e);
      });
    };

    // fetch collab data
    const fetchCollab = async () => {
      setIsLoading(true);
      try {
        const { data: resData } = await axios.get(
          `${host}/influencer/collab/${link}`
        );
        if (resData?.success) {
          setCollabFeedbackData({
            collabData: resData?.data?.collabData,
            influencerData: resData?.data?.influencerData,
          });

          // Set local storage name
          setLocalStorageName(link + "_rating");
          // Get saved rating and review from local storage
          const savedRating = JSON.parse(
            localStorage.getItem(localStorageName)
          );
          // Set rating and review
          setRating(
            savedRating?.rating ||
              resData?.data?.collabData?.collabVerification?.rating ||
              0
          );
          setReview(
            savedRating?.review ||
              resData?.data?.collabData?.collabVerification?.review ||
              ""
          );
        } else {
          toast.error(resData?.error || resData?.message);
        }
        setIsLoading(false);
      } catch (error) {
        console.error(error);
        toast.error("Something went wrong", { autoClose: 2000 });
      }
    };

    if (link) {
      fetchCollab();
      fetchMasterData();
    }
  }, [link]);

  const handleSubmitRating = async () => {
    mixpanel.track("submitted collab verify rating", {
      link: link,
    });
    // Save rating and review in local storage
    const ratingPayload = {
      rating: rating,
      review: review,
    };
    // Save rating and review in local storage
    localStorage.setItem(localStorageName, JSON.stringify(ratingPayload));

    // Check if user is logged in
    if (
      localStorage.getItem("jwtToken") &&
      localStorage.getItem("UserType").toLocaleLowerCase() === "brand"
    ) {
      // Proceed to submit rating
      try {
        const res = await axios.post(
          `${host}/Brand/collab/${collabFeedbackData?.collabData?._id}/rating`,
          {
            rating: rating,
            review: review,
          },
          {
            headers: {
              jwtToken: localStorage.getItem("jwtToken"),
            },
          }
        );

        // Check if the response is successful
        if (res?.data?.success) {
          localStorage.removeItem(localStorageName);
          toast.success("Rating submitted successfully!");
          navigate("/brand/rating/success", { replace: true });
        }
      } catch (error) {
        // User Unauthorized
        setOpenLoginPopup(true);
        toast.error(error?.response?.data?.error || "Something went wrong!");
      }
    } else {
      // Open Login Popup
      setOpenLoginPopup(true);
    }
  };

  return (
    <>
      {openLoginPopup && (
        <Dialog open={openLoginPopup} onOpenChange={setOpenLoginPopup}>
          <LoginForVerifyCollab navigateUrl={location.pathname} />
        </Dialog>
      )}
      <div className="sticky top-0 left-0 right-0">
        <BrandHeader />
      </div>
      <div key="1" className="flex flex-col min-h-[100dvh]">
        <main className="flex-1 container px-4 md:px-6 py-12 md:py-16">
          <div className="max-w-2xl mx-auto space-y-5">
            {/* Already Verified Collab Top */}
            {collabFeedbackData?.collabData?.collabVerification?.status ===
              1 && <VerifiedCollabTop />}

            {/* Collab Data */}
            <div className="text-center space-y-4">
              <h1 className="text-3xl font-bold tracking-tighter sm:text-4xl md:text-5xl">
                Past Brand Collaboration
                <br />
                {` ${
                  collabFeedbackData?.influencerData?.name?.split(" ")?.[0]
                } `}
                {"<>"}
                {` ${
                  collabFeedbackData?.collabData?.companyName?.split(" ")?.[0]
                } `}
              </h1>
              <div className="flex items-center justify-center gap-2">
                <div className="text-sm font-medium">
                  Influencer: {collabFeedbackData?.influencerData?.name}
                </div>
                {collabFeedbackData?.influencerData?.linkedinLink && (
                  <a
                    href={collabFeedbackData?.influencerData?.linkedinLink}
                    target="_blank"
                    onClick={() => {
                      mixpanel.track("Clicked on influencer linkedin profile");
                    }}
                  >
                    <LinkedinIcon className="h-4 w-4" />
                  </a>
                )}
              </div>
              <div className="grid gap-2">
                <div className="text-sm font-medium">Campaign Goals</div>
                <div className="flex flex-wrap gap-2 justify-center">
                  {collabFeedbackData?.collabData?.goals?.length > 0 &&
                    collabFeedbackData?.collabData?.goals?.map(
                      (goal, index) => {
                        const GoalName = masterCollabGoals?.find(
                          (mastergoal) => mastergoal?._id === goal
                        );
                        return (
                          <div
                            key={index}
                            className="bg-gray-100 px-3 py-1 rounded-full text-sm font-medium dark:bg-gray-800 dark:text-gray-200"
                          >
                            {GoalName?.CollabGoalsName}
                          </div>
                        );
                      }
                    )}
                </div>
              </div>
            </div>

            {/* Collab Rating Form */}
            {collabFeedbackData?.collabData?.collabVerification?.status ===
              0 && (
              <>
                <div className="flex items-center justify-center gap-2 text-4xl">
                  {Array.from({ length: 5 }).map((_, index) => (
                    <StarIcon
                      key={index}
                      className={twMerge(
                        `w-8 h-8`,
                        `${
                          rating >= index + 1
                            ? "fill-primary"
                            : "fill-muted stroke-muted-foreground"
                        }`
                      )}
                      onClick={() => setRating(index + 1)}
                    />
                  ))}
                </div>
                {rating > 0 && (
                  <div>
                    <Label htmlFor="review">Any feedback or suggestions</Label>
                    <Textarea
                      id="review"
                      placeholder={`Write what you liked or disliked about this collaboration with ${collabFeedbackData?.influencerData?.name}`}
                      value={review}
                      onChange={(e) => setReview(e.target.value)}
                    />
                  </div>
                )}
                {
                  <div className="flex justify-center">
                    <Button
                      className="w-full max-w-[200px] bg-black hover:bg-black text-white"
                      type="submit"
                      disabled={rating === 0}
                      onClick={() => handleSubmitRating()}
                    >
                      Submit
                    </Button>
                  </div>
                }
              </>
            )}

            {/* Already Verified Collab Bottom */}
            {collabFeedbackData?.collabData?.collabVerification?.status ===
              1 && (
              <VerifiedCollabBottom
                review={
                  collabFeedbackData?.collabData?.collabVerification?.review
                }
                rating={
                  collabFeedbackData?.collabData?.collabVerification?.rating
                }
              />
            )}
          </div>
        </main>
      </div>
    </>
  );
};

const VerifiedCollabTop = () => {
  return (
    <div className="flex flex-col items-center">
      <CheckCircleIcon className="w-16 h-16 text-green-500" />
      <h1 className="text-3xl font-bold">Verified</h1>
      <p className="text-gray-500 dark:text-gray-400">
        This collaboration is verified by the company
      </p>
    </div>
  );
};

const VerifiedCollabBottom = ({ review, rating }) => {
  return (
    <>
      <div className="flex items-center justify-center gap-2 text-4xl">
        <StarIcon
          className={twMerge(
            `w-8 h-8`,
            `${
              rating >= 1
                ? "fill-primary"
                : "fill-muted stroke-muted-foreground"
            }`
          )}
        />
        <StarIcon
          className={twMerge(
            `w-8 h-8`,
            `${
              rating >= 2
                ? "fill-primary"
                : "fill-muted stroke-muted-foreground"
            }`
          )}
        />
        <StarIcon
          className={twMerge(
            `w-8 h-8`,
            `${
              rating >= 3
                ? "fill-primary"
                : "fill-muted stroke-muted-foreground"
            }`
          )}
        />
        <StarIcon
          className={twMerge(
            `w-8 h-8`,
            `${
              rating >= 4
                ? "fill-primary"
                : "fill-muted stroke-muted-foreground"
            }`
          )}
        />
        <StarIcon
          className={twMerge(
            `w-8 h-8`,
            `${
              rating >= 5
                ? "fill-primary"
                : "fill-muted stroke-muted-foreground"
            }`
          )}
        />
      </div>
      {review && (
        <div className="flex flex-col items-center justify-center gap-2">
          <Label>Feedback or suggestions</Label>
          <p className="text-gray-500 dark:text-gray-400">{review}</p>
        </div>
      )}
      <div className="flex justify-center items-center">
        <a
          className="inline-flex h-10 items-center justify-center rounded-md bg-gray-900 px-8 text-sm font-medium text-gray-50 shadow transition-colors hover:bg-gray-900/90 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-gray-950 disabled:pointer-events-none disabled:opacity-50 dark:bg-gray-50 dark:text-gray-900 dark:hover:bg-gray-50/90 dark:focus-visible:ring-gray-300"
          href="https://www.anchors.in/waitlist/?ref=collab_get_verified"
        >
          Explore now
        </a>
      </div>
    </>
  );
};
